import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { User, Mail, Phone, Users, MapPin, Euro, Tag, UserPlus, UserCheck, Briefcase } from 'lucide-react';
import { cn } from '@/components/utils/utils';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/utils/supabase-functions';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface AffiliatedAdvisor {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface CreateClientRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface ExistingClient {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
}

export const CreateClientRequestModal = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateClientRequestModalProps) => {
  const { t } = useI18n();
  const { userRole, user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [clientMode, setClientMode] = useState<'new' | 'existing'>('new');
  const [existingClients, setExistingClients] = useState<ExistingClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [affiliatedAdvisors, setAffiliatedAdvisors] = useState<AffiliatedAdvisor[]>([]);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string>('__self__');
  
  // Client data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  // Travel request data (solo destinazioni obbligatorie)
  const [destinations, setDestinations] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [totalGuests, setTotalGuests] = useState('');
  const [interests, setInterests] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Load existing clients when modal opens in 'existing' mode
  useEffect(() => {
    if (isOpen && clientMode === 'existing') {
      loadExistingClients();
    }
  }, [isOpen, clientMode]);

  // Load affiliated advisors when agency opens the modal
  useEffect(() => {
    if (isOpen && userRole === 'agency' && user?.id) {
      loadAffiliatedAdvisors();
    }
  }, [isOpen, userRole, user?.id]);

  const loadAffiliatedAdvisors = async () => {
    try {
      // Usa la RPC SECURITY DEFINER get_my_advisors invece di interrogare
      // direttamente user_public_profile + users: la RLS su public.users
      // non concede all'agency la visibilità sulle righe travel_advisor_member,
      // quindi la query diretta tornava sempre vuota (vedi TravelRequestModal.tsx).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: advisors, error: advisorsError } = await supabase.rpc('get_my_advisors' as any);

      if (advisorsError) throw advisorsError;
      setAffiliatedAdvisors((advisors as AffiliatedAdvisor[]) || []);
    } catch {
      toast({ title: t('common.error'), description: 'Errore nel caricamento degli advisor', variant: 'destructive' });
    }
  };

  const loadExistingClients = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, phone')
        .eq('role', 'traveler')
        .order('first_name', { ascending: true });

      if (error) throw error;
      setExistingClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast({
        title: t('common.error'),
        description: 'Errore nel caricamento dei clienti',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setClientMode('new');
    setSelectedClientId('');
    setSelectedAdvisorId('__self__');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setDestinations('');
    setStartDate('');
    setEndDate('');
    setBudgetMin('');
    setBudgetMax('');
    setTotalGuests('');
    setInterests('');
    setAdditionalNotes('');
  };

  const handleClientModeChange = (mode: 'new' | 'existing') => {
    setClientMode(mode);
    // Reset client selection quando cambio tab
    if (mode === 'new') {
      setSelectedClientId('');
    } else {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
    }
  };

  const validateForm = () => {
    // Validazione cliente
    if (clientMode === 'new') {
      if (!firstName.trim() || !lastName.trim() || !email.trim() || !phone.trim()) {
        toast({
          title: t('common.error'),
          description: 'Tutti i dati del cliente sono obbligatori',
          variant: 'destructive',
        });
        return false;
      }
    } else {
      if (!selectedClientId) {
        toast({
          title: t('common.error'),
          description: 'Seleziona un cliente esistente',
          variant: 'destructive',
        });
        return false;
      }
    }

    // Solo destinazioni obbligatorie
    if (!destinations.trim()) {
      toast({
        title: t('common.error'),
        description: 'Le destinazioni sono obbligatorie',
        variant: 'destructive',
      });
      return false;
    }

    // Validazioni opzionali (se compilate)
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      toast({
        title: t('common.error'),
        description: 'La data di fine deve essere successiva alla data di inizio',
        variant: 'destructive',
      });
      return false;
    }

    if (budgetMin && budgetMax && parseInt(budgetMin) > parseInt(budgetMax)) {
      toast({
        title: t('common.error'),
        description: 'Il budget minimo non può essere superiore al massimo',
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: t('common.error'),
          description: 'Sessione scaduta',
          variant: 'destructive',
        });
        return;
      }

      // Prepara i dati del cliente (nuovo o esistente)
      let clientData;
      if (clientMode === 'existing') {
        const selectedClient = existingClients.find(c => c.id === selectedClientId);
        if (!selectedClient) {
          toast({
            title: t('common.error'),
            description: 'Cliente selezionato non trovato',
            variant: 'destructive',
          });
          return;
        }
        clientData = {
          existingClientId: selectedClientId,
          firstName: selectedClient.first_name,
          lastName: selectedClient.last_name,
          email: selectedClient.email,
          phone: selectedClient.phone || '',
        };
      } else {
        clientData = {
          firstName,
          lastName,
          email,
          phone,
        };
      }

      await invokeEdgeFunction(supabase, 'create-client-request', {
        body: {
          client: clientData,
          travelRequest: {
            destinations: destinations.split(',').map(d => d.trim()).filter(Boolean),
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            budgetMin: budgetMin ? parseInt(budgetMin) : undefined,
            budgetMax: budgetMax ? parseInt(budgetMax) : undefined,
            totalGuests: totalGuests ? parseInt(totalGuests) : undefined,
            interests: interests ? interests.split(',').map(i => i.trim()).filter(Boolean) : [],
            additionalNotes: additionalNotes || undefined,
          },
          ...(userRole === 'agency' && selectedAdvisorId && selectedAdvisorId !== '__self__' ? { assignedDesignerId: selectedAdvisorId } : {}),
        },
      });

      const clientName = clientMode === 'existing' 
        ? existingClients.find(c => c.id === selectedClientId)?.first_name + ' ' + existingClients.find(c => c.id === selectedClientId)?.last_name
        : `${firstName} ${lastName}`;

      toast({
        title: clientMode === 'existing' ? 'Richiesta creata!' : 'Cliente creato!',
        description: clientMode === 'existing'
          ? `Nuova richiesta creata per ${clientName}`
          : `Cliente ${clientName} creato con successo. Riceverà la notifica quando invierai la prima quote.`,
      });

      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error creating client:', error);
      toast({
        title: t('common.error'),
        description: (error as Error).message || t('common.error_occurred'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  // Validazione per abilitare il bottone submit
  const isFormValid = (() => {
    // Verifica dati cliente
    const hasValidClient = clientMode === 'new'
      ? firstName.trim() && lastName.trim() && email.trim() && phone.trim()
      : selectedClientId;
    
    // Verifica destinazioni (campo obbligatorio)
    const hasDestinations = destinations.trim();
    
    return hasValidClient && hasDestinations;
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Crea Richiesta di Viaggio"
      size="lg"
    >
      <div className="flex flex-col h-[600px]">
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Client Mode Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => handleClientModeChange("new")}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all",
                clientMode === "new"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900",
              )}
            >
              <UserPlus className="h-4 w-4" />
              Nuovo Cliente
            </button>
            <button
              type="button"
              onClick={() => handleClientModeChange("existing")}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all",
                clientMode === "existing"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900",
              )}
            >
              <UserCheck className="h-4 w-4" />
              Cliente Esistente
            </button>
          </div>

          {/* Client Section */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
              <User className="h-4 w-4" />
              {clientMode === "new"
                ? "Dati Nuovo Cliente"
                : "Seleziona Cliente"}
            </h3>

            {clientMode === "existing" ? (
              <div className="space-y-2">
                <Label htmlFor="existingClient" className="text-xs">
                  Cliente *
                </Label>
                <Select
                  value={selectedClientId}
                  onValueChange={setSelectedClientId}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Seleziona un cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {existingClients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClientId && (
                  <p className="text-xs text-gray-500">
                    ✓ Cliente selezionato:{" "}
                    {
                      existingClients.find((c) => c.id === selectedClientId)
                        ?.first_name
                    }{" "}
                    {
                      existingClients.find((c) => c.id === selectedClientId)
                        ?.last_name
                    }
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-xs">
                      Nome *
                    </Label>
                    <Input
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Mario"
                      disabled={isLoading}
                      className="h-9"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-xs">
                      Cognome *
                    </Label>
                    <Input
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Rossi"
                      disabled={isLoading}
                      className="h-9"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="email"
                      className="text-xs flex items-center gap-1"
                    >
                      <Mail className="h-3 w-3" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="mario.rossi@example.com"
                      disabled={isLoading}
                      className="h-9"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="phone"
                      className="text-xs flex items-center gap-1"
                    >
                      <Phone className="h-3 w-3" />
                      Telefono *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+39 123 456 7890"
                      disabled={isLoading}
                      className="h-9"
                    />
                  </div>
                </div>

                <p className="text-xs text-gray-500 italic">
                  Il cliente NON riceverà email finché non invii la prima quote
                </p>
              </>
            )}
          </div>

          {/* Advisor Section — visible only for agency accounts */}
          {userRole === 'agency' && (
            <div className="bg-purple-50 rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-sm text-purple-700 flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                {t('travelRequest.assignToLabel')}
              </h3>
              <Select value={selectedAdvisorId} onValueChange={setSelectedAdvisorId}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__self__">{t('travelRequest.agencyMyself')}</SelectItem>
                  {affiliatedAdvisors.map((advisor) => (
                    <SelectItem key={advisor.id} value={advisor.id}>
                      {advisor.first_name} {advisor.last_name} ({advisor.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Travel Request Section */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-4">
            <h3 className="font-semibold text-sm text-blue-700 flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Dettagli Viaggio
            </h3>

            {/* Destinazioni - PRIMO E UNICO CAMPO OBBLIGATORIO */}
            <div className="space-y-2">
              <Label
                htmlFor="destinations"
                className="text-xs flex items-center gap-1 font-semibold"
              >
                <MapPin className="h-3 w-3" />
                Destinazioni * (separate da virgola)
              </Label>
              <Input
                id="destinations"
                value={destinations}
                onChange={(e) => setDestinations(e.target.value)}
                placeholder="Es: Roma, Venezia, Firenze"
                disabled={isLoading}
                className="h-9 border-blue-300 focus:border-blue-500"
              />
            </div>

            {/* Campi opzionali */}
            <div className="pt-2 border-t border-blue-200">
              <p className="text-xs text-blue-600 mb-3">
                Informazioni aggiuntive (opzionali)
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-xs">
                    Data Inizio
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-xs">
                    Data Fine
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="totalGuests"
                    className="text-xs flex items-center gap-1"
                  >
                    <Users className="h-3 w-3" />
                    Ospiti
                  </Label>
                  <Input
                    id="totalGuests"
                    type="number"
                    min="1"
                    value={totalGuests}
                    onChange={(e) => setTotalGuests(e.target.value)}
                    placeholder="2"
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="budgetMin"
                    className="text-xs flex items-center gap-1"
                  >
                    <Euro className="h-3 w-3" />
                    Budget Min
                  </Label>
                  <Input
                    id="budgetMin"
                    type="number"
                    min="0"
                    value={budgetMin}
                    onChange={(e) => setBudgetMin(e.target.value)}
                    placeholder="1000"
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="budgetMax"
                    className="text-xs flex items-center gap-1"
                  >
                    <Euro className="h-3 w-3" />
                    Budget Max
                  </Label>
                  <Input
                    id="budgetMax"
                    type="number"
                    min="0"
                    value={budgetMax}
                    onChange={(e) => setBudgetMax(e.target.value)}
                    placeholder="3000"
                    disabled={isLoading}
                    className="h-9"
                  />
                </div>
              </div>

              <div className="space-y-2 mt-4">
                <Label
                  htmlFor="interests"
                  className="text-xs flex items-center gap-1"
                >
                  <Tag className="h-3 w-3" />
                  Interessi (separati da virgola)
                </Label>
                <Input
                  id="interests"
                  value={interests}
                  onChange={(e) => setInterests(e.target.value)}
                  placeholder="Arte, Cibo, Natura"
                  disabled={isLoading}
                  className="h-9"
                />
              </div>

              <div className="space-y-2 mt-4">
                <Label htmlFor="notes" className="text-xs">
                  Note aggiuntive
                </Label>
                <Textarea
                  id="notes"
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Eventuali richieste o preferenze particolari..."
                  disabled={isLoading}
                  rows={3}
                  className="resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-2 p-4 border-t border-gray-200">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !isFormValid}>
            {isLoading
              ? "Creazione..."
              : clientMode === "existing"
                ? "Crea Richiesta"
                : "Crea Cliente + Richiesta"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
